import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';

import { getClimateExposureLevel } from '../utils/monitoringResultsUtils';

import type IMonitoringClimateEntityCard from './interfaces/IMonitoringClimateEntityCard';

import styles from './sass/MonitoringClimateEntityCard.module.scss';

const SCORE_CLASS_BY_LEVEL: Record<'low' | 'mid' | 'high', string> = {
  low: 'scoreLow',
  mid: 'scoreMid',
  high: 'scoreHigh',
};

const formatLocation = (
  location?: { country?: string, region?: string | null, locality?: string | null },
): string => {
  if (!location) {
    return '—';
  }

  return [location.locality, location.region, location.country]
    .filter((part): part is string => Boolean(part && String(part).trim()))
    .join(', ') || '—';
};

function MonitoringClimateEntityCard({
  entity,
  defaultExpanded = getClimateExposureLevel(entity.exposure_score) === 'high',
}: IMonitoringClimateEntityCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const profile = entity.climate_profile;
  const scoreLevel = getClimateExposureLevel(entity.exposure_score);
  const trendKey = entity.trend === 'declining' || entity.trend === 'rising' || entity.trend === 'stable'
    ? entity.trend
    : 'stable';
  const kindKey = entity.entity_kind || 'supplier';

  return (
    <article className={styles.container}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{entity.name}</h3>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles.themeBadge)}>
              {t(`valueChainMap.monitoring.results.climateEntityKind.${kindKey}`, {
                defaultValue: kindKey,
              })}
            </span>
            <span className={clsx(styles.badge, styles[SCORE_CLASS_BY_LEVEL[scoreLevel]])}>
              {t('valueChainMap.monitoring.results.climateExposureScore', {
                value: entity.exposure_score,
              })}
            </span>
            <span className={clsx(styles.badge, styles[`trend_${trendKey}`])}>
              {t(`valueChainMap.monitoring.results.climateTrend.${trendKey}`)}
            </span>
            {entity.spike ? (
              <span className={clsx(styles.badge, styles.spikeBadge)}>
                {t('valueChainMap.monitoring.results.climateSpike')}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          <div className={styles.snapshotGrid}>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.climateLocation')}
              </span>
              <span className={styles.snapshotValue}>{formatLocation(entity.location)}</span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.climateHorizon')}
              </span>
              <span className={styles.snapshotValue}>{profile.dominant_horizon || '—'}</span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.climateMaterials')}
              </span>
              <span className={styles.snapshotValue}>
                {(entity.related_materials || []).join(' · ') || '—'}
              </span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.climateHazards')}
              </span>
              <span className={styles.snapshotValue}>
                {(profile.hazards_active || []).map((hazard) => (
                  t(`valueChainMap.monitoring.results.climateTheme.${hazard}`, {
                    defaultValue: hazard,
                  })
                )).join(' · ') || '—'}
              </span>
            </div>
          </div>

          {profile.notes ? (
            <p className={styles.mechanism}>{profile.notes}</p>
          ) : null}

          {(entity.alerts || []).length ? (
            <div className={styles.groupPreviewList}>
              {entity.alerts!.map((alert) => (
                <span
                  key={`${entity.unitId}-${alert.title}`}
                  className={clsx(styles.groupChip, {
                    [styles.groupChipFlagged]: alert.severity === 'high' || alert.severity === 'critical',
                  })}
                >
                  {alert.theme}
                  {': '}
                  {alert.title}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringClimateEntityCard;
